import { Injectable } from '@angular/core';
import { DialogParams, DialogService } from '../../../core/services/dialog.service';
import { EditTrackerService } from '../../../core/services/edit-tracker.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { MessagingService } from '../../../core/services/messaging.service';
import { PermissionService } from '../../../core/services/permission';
import { FeatureService } from '../../../core/services/permission';
import { SmsService } from '../../../core/services/sms.service';
import { TriallerService } from '../../../core/services/trialler.service';
import { UserService } from '../../../core/services/user';
import { RepoAccountService } from '../../../data/repositories/repo-account.service';
import { SharedDataService } from '../../../data/services/shared-data.service';

export interface SubpanelOption {
    label: string;
    route: string;
    subRoute: string;
    count?: number;
}

/** This service has shared methods between the side bar sub-menu for mobile view and the sunpanel in desktop view */

@Injectable({ providedIn: 'root' })
export class Sidebar2SubpanelSharedService {
    constructor(
        private accountRepo: RepoAccountService,
        private sharedData: SharedDataService,
        private featureService: FeatureService,
        private editTrackerService: EditTrackerService,
        private dialogService: DialogService,
        private messagingService: MessagingService,
        private triallerSerive: TriallerService,
        private lds: LaunchDarklyService,
        private smsService: SmsService,
        private permissionService: PermissionService,
        private userService: UserService
    ) {}

    resetInvoiceCounts() {
        const invoiceCounts = {};
        // @ts-ignore TS7053
        invoiceCounts['all'] = { Status: 'All', count: 0 };
        // @ts-ignore TS7053
        invoiceCounts['pending'] = { Status: 'Pending', count: 0 };
        // @ts-ignore TS7053
        invoiceCounts['invoiced'] = { Status: 'Invoiced', count: 0 };
        // @ts-ignore TS7053
        invoiceCounts['partreceived'] = { Status: 'Part Received', count: 0 };
        // @ts-ignore TS7053
        invoiceCounts['received'] = { Status: 'Received', count: 0 };
        return invoiceCounts;
    }

    resetOrderCounts() {
        const orderCounts = {};
        // @ts-ignore TS7053
        orderCounts['all'] = { Status: 'All', count: 0 };
        // @ts-ignore TS7053
        orderCounts['unsent'] = { Status: 'Unsent', count: 0 };
        // @ts-ignore TS7053
        orderCounts['sent'] = { Status: 'Sent', count: 0 };
        // @ts-ignore TS7053
        orderCounts['partreceived'] = { Status: 'Part Received', count: 0 };
        // @ts-ignore TS7053
        orderCounts['received'] = { Status: 'Received', count: 0 };
        // @ts-ignore TS7053
        orderCounts['cancelled'] = { Status: 'Cancelled', count: 0 };
        // @ts-ignore TS7053
        orderCounts['completed'] = { Status: 'Completed', count: 0 };
        return orderCounts;
    }

    getVisibilityOfConnections() {
        let active = false;
        if (this.sharedData.Integrations && this.sharedData.Integrations.length) {
            this.sharedData.Integrations.map((r) => {
                if (r.status.state !== 'NotConnected') active = true;
            });
        }
        return active;
    }

    getNotificationBadges() {
        const communicationsOptions = {
            Communications: [] as SubpanelOption[],
        };

        const smsPermission =
            this.permissionService.isAvailable('messages.maintain-emails.sms-menu') && this.featureService.isAvailable('sms-features');
        const emailPermission = this.permissionService.isAvailable('messages.maintain-emails.email-menu');
        const conversationsCheck = this.featureService.getState('clientportal');

        if (emailPermission) {
            communicationsOptions.Communications.push({
                label: 'Email',
                route: '/messages/email',
                subRoute: 'email',
                count: this.sharedData.UnreadCounts.emails,
            });

            communicationsOptions.Communications.push({
                label: 'Email Senders',
                route: '/messages/email-senders',
                subRoute: 'email-senders',
            });
        }
        if (smsPermission && this.userService.isSmsEnabled) {
            communicationsOptions.Communications.push({
                label: 'SMS',
                route: '/messages/sms',
                subRoute: 'sms',
            });
        }
        communicationsOptions.Communications.push({
            label: 'Notifications',
            route: '/messages/notifications',
            subRoute: 'notifications',
            count: this.sharedData.UnreadCounts.messages,
        });
        if (conversationsCheck) {
            communicationsOptions.Communications.push({
                label: 'Conversations',
                route: '/messages/conversations',
                subRoute: 'conversations',
                count: this.sharedData.UnreadCounts.conversations,
            });
        }

        return communicationsOptions;
    }

    signOut() {
        // Sign out
        let txt = 'Are you sure you want to log out?';
        if (this.editTrackerService.isTracking && !this.editTrackerService.allowStateChange) {
            txt = `You have unsaved changes. ${txt}`;
        }
        const params: DialogParams = {
            type: 'warning',
            title: 'Confirm Log Out',
            text: txt,
            okText: 'Yes',
            cancelText: 'No',
        };
        this.dialogService.show(params).then(
            () => {
                this.accountRepo.logOut();
            },
            () => {}
        );
    }

    getAccountOptions() {
        const businessSetup = {
            'Business Account': [
                {
                    label: 'Business Details',
                    route: '/account/details',
                    subRoute: 'details',
                },
                {
                    label: 'Users',
                    route: '/account/users',
                    subRoute: 'users',
                },
                {
                    label: 'Subscriptions',
                    route: '/account/subscription',
                    subRoute: 'subscription',
                },
            ],
            'Business Setup': [
                {
                    label: 'Business Documents',
                    route: '/account/setup/businessdocuments',
                    subRoute: 'business-documents',
                },
                {
                    label: 'Building Types',
                    route: '/account/setup/buildingtypes',
                    subRoute: 'building-types',
                },
                {
                    label: 'Contact Groups',
                    route: '/account/setup/contactgroups',
                    subRoute: 'contact-groups',
                },
                {
                    label: 'Contact Types',
                    route: '/account/setup/contacttypes',
                    subRoute: 'contact-types',
                },
                {
                    label: 'Client Sources',
                    route: '/account/setup/customersources',
                    subRoute: 'customer-sources',
                },
                {
                    label: 'Client Types',
                    route: '/account/setup/customertypes',
                    subRoute: 'customer-types',
                },
                {
                    label: 'Unit Measures',
                    route: '/account/setup/unitmeasures',
                    subRoute: 'unit-measures',
                },
                {
                    label: 'Tax Rates',
                    route: '/account/setup/taxrates',
                    subRoute: 'tax-rates',
                },
            ],
            'Business API': [
                {
                    label: 'Webhooks',
                    route: '/account/api/webhooks',
                    subRoute: 'api-webhooks',
                },
                {
                    label: 'Event Log',
                    route: '/account/api/eventlog',
                    subRoute: 'api-eventlog',
                },
            ],
        };

        return businessSetup;
    }
}
